/**
 * Top Artists Chart Page — Phase B (2026-02-24)
 *
 * Routes:
 *   /top-artists           → redirect to /top-artists/greek
 *   /top-artists/greek     → Greek chart
 *   /top-artists/global    → Global chart
 *
 * Data source:
 *   GET /api/charts/top-artists?scope=greek|global&range=<key>&limit=50
 *   GET /api/charts/top-artists/trend?canonical_artist=<x>&days=30
 *
 * Design principles:
 *   • Names shown VERBATIM (Apple/Shazam Latin form). No case rewriting.
 *   • Cover art comes from Shazam/iTunes cached URL in the response.
 *   • Trend arrow (↑/↓/flat) and top song appear ONLY in the expanded
 *     row (lazy-loaded), because Phase A does not embed movement in
 *     the list response.
 *   • NEW / PEAK badges reserved for Phase C (not yet in backend).
 */

import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useParams, useNavigate, Navigate, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { toast } from 'sonner';
import {
  Music, TrendingUp, TrendingDown, Minus, Users, Radio,
  ChevronDown, ChevronUp, Search, Download, Trophy, Loader2,
  Globe2, Flag,
} from 'lucide-react';
import { getBackendUrl } from '../utils/api';
import { useLanguage } from '../contexts/LanguageContext';

const API = getBackendUrl();

const RANGE_OPTIONS = [
  { key: 'weekly',     labelKey: 'topArtistsRangeWeekly' },
  { key: 'last_week',  labelKey: 'topArtistsRangeLastWeek' },
  { key: 'this_month', labelKey: 'topArtistsRangeThisMonth' },
  { key: 'last_month', labelKey: 'topArtistsRangeLastMonth' },
  { key: '3m',         labelKey: 'topArtistsRange3m' },
  { key: '6m',         labelKey: 'topArtistsRange6m' },
  { key: '12m',        labelKey: 'topArtistsRange12m' },
];

const SCOPE_META_STATIC = {
  greek:  { icon: Flag,    color: 'from-blue-500 to-cyan-500' },
  global: { icon: Globe2,  color: 'from-purple-500 to-pink-500' },
};

// ─── Sparkline for expanded row ─────────────────────────────────
const Sparkline = ({ series, width = 220, height = 44, idKey = '', emptyLabel = 'Insufficient data' }) => {
  const points = (series || []).map(s => Number(s.plays) || 0);
  if (points.length < 2) {
    return <span className="text-gray-500 text-xs italic">{emptyLabel}</span>;
  }
  const max = Math.max(...points, 1);
  const min = Math.min(...points);
  const padding = 3;
  const usableW = width - padding * 2;
  const usableH = height - padding * 2;
  const coords = points.map((p, i) => {
    const x = padding + (i / Math.max(points.length - 1, 1)) * usableW;
    const y = padding + (1 - (p - min) / Math.max(max - min, 1)) * usableH;
    return { x, y };
  });
  const path = coords.map((c, i) => `${i === 0 ? 'M' : 'L'}${c.x.toFixed(1)},${c.y.toFixed(1)}`).join(' ');
  const last = coords[coords.length - 1];
  const first = coords[0];
  const up = points[points.length - 1] >= points[0];
  const color = up ? '#22c55e' : '#ef4444';
  // Unique gradient id per artist/width prevents SVG defs collision if
  // multiple sparklines co-exist on the page.
  const gradientId = `spk-${idKey || 'x'}-${width}`.replace(/[^a-zA-Z0-9_-]/g, '_');

  return (
    <svg width={width} height={height} className="inline-block">
      <defs>
        <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={color} stopOpacity="0.35" />
          <stop offset="100%" stopColor={color} stopOpacity="0" />
        </linearGradient>
      </defs>
      <path d={`${path} L${last.x.toFixed(1)},${height} L${first.x.toFixed(1)},${height} Z`} fill={`url(#${gradientId})`} />
      <path d={path} fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      <circle cx={last.x} cy={last.y} r="3" fill={color} stroke="white" strokeWidth="1" />
    </svg>
  );
};

// ─── Expanded row: movement + top song ───────────────────────────
const ArtistTrendPanel = ({ artist, rangeKey, t }) => {
  const [data, setData]     = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setData(null);
    const days = rangeKey === '12m' ? 90 : rangeKey === '6m' ? 90 : rangeKey === '3m' ? 60 : 30;
    const url = `${API}/api/charts/top-artists/trend?canonical_artist=${encodeURIComponent(artist.canonical_artist)}&days=${days}`;
    fetch(url)
      .then(r => (r.ok ? r.json() : Promise.reject(r.status)))
      .then(d => { if (!cancelled) setData(d); })
      .catch(() => { if (!cancelled) setData({ series: [], top_song: null, movement: null }); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [artist.canonical_artist, rangeKey]);

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-gray-400 text-sm p-4" data-testid="trend-loading">
        <Loader2 className="w-4 h-4 animate-spin" />
        <span>{t('topArtistsTrendLoading') || 'Loading history…'}</span>
      </div>
    );
  }

  const movement = data?.movement;
  const topSong  = data?.top_song;
  const series   = data?.series || [];

  const dirIcon =
    movement?.direction === 'up'   ? <TrendingUp   className="w-4 h-4 text-green-400" /> :
    movement?.direction === 'down' ? <TrendingDown className="w-4 h-4 text-red-400" />   :
                                     <Minus         className="w-4 h-4 text-gray-400" />;
  const dirColor =
    movement?.direction === 'up'   ? 'text-green-400' :
    movement?.direction === 'down' ? 'text-red-400'   : 'text-gray-300';

  return (
    <div
      className="bg-gradient-to-r from-purple-900/40 to-gray-900/60 border-t border-b border-purple-500/20 px-4 py-4"
      data-testid={`trend-panel-${artist.canonical_artist}`}
    >
      <div className="flex flex-col lg:flex-row gap-4 items-start">
        {/* Sparkline */}
        <div className="flex-shrink-0">
          <div className="text-[10px] text-gray-500 mb-1 uppercase tracking-wider">
            {t('topArtistsTrendHistory') || 'Plays history'}
          </div>
          <div className="bg-black/30 rounded-lg p-2 min-w-[240px]">
            <Sparkline series={series} width={220} height={44} idKey={artist.canonical_artist} emptyLabel={t('topArtistsInsufficientData') || 'Insufficient data'} />
            {series.length > 0 && (
              <div className="flex justify-between text-[9px] text-gray-600 mt-1 px-1">
                <span>{series[0]?.date?.slice(5)}</span>
                <span>{series[series.length - 1]?.date?.slice(5)}</span>
              </div>
            )}
          </div>
        </div>

        {/* Movement stats */}
        <div className="flex-1 grid grid-cols-2 sm:grid-cols-3 gap-3">
          <div className="bg-white/5 rounded-lg p-2 text-center">
            <div className="text-[10px] text-gray-500 uppercase">{t('topArtistsMovement') || 'Movement'}</div>
            <div className="flex items-center justify-center gap-1 mt-1">
              {dirIcon}
              <span className={`text-sm font-bold ${dirColor}`} data-testid="movement-delta">
                {movement?.delta != null
                  ? (movement.delta > 0 ? `+${movement.delta}` : `${movement.delta}`)
                  : '—'}
              </span>
            </div>
            <div className="text-[9px] text-gray-500 mt-0.5">{t('topArtistsMovement7d') || '7d vs previous 7d'}</div>
          </div>
          <div className="bg-white/5 rounded-lg p-2 text-center">
            <div className="text-[10px] text-gray-500 uppercase">{t('topArtistsRecent7d') || 'Recent 7d'}</div>
            <div className="text-sm font-bold text-purple-300 mt-1">
              {movement?.recent_7d_plays ?? '—'}
            </div>
            <div className="text-[9px] text-gray-500 mt-0.5">plays</div>
          </div>
          <div className="bg-white/5 rounded-lg p-2 text-center">
            <div className="text-[10px] text-gray-500 uppercase">{t('topArtistsPrevious7d') || 'Previous 7d'}</div>
            <div className="text-sm font-bold text-gray-300 mt-1">
              {movement?.previous_7d_plays ?? '—'}
            </div>
            <div className="text-[9px] text-gray-500 mt-0.5">plays</div>
          </div>
        </div>

        {/* Top song */}
        <div className="flex-shrink-0 sm:max-w-[280px] w-full lg:w-auto">
          <div className="text-[10px] text-gray-500 mb-1 uppercase tracking-wider">{t('topArtistsTopSong') || 'Top song'}</div>
          {topSong ? (
            <div className="flex items-center gap-3 bg-white/5 rounded-lg p-2" data-testid="top-song-panel">
              {topSong.cover_art_url ? (
                <img
                  src={topSong.cover_art_url}
                  alt=""
                  className="w-12 h-12 rounded object-cover flex-shrink-0"
                  loading="lazy"
                />
              ) : (
                <div className="w-12 h-12 rounded bg-white/10 flex items-center justify-center flex-shrink-0">
                  <Music className="w-5 h-5 text-white/40" />
                </div>
              )}
              <div className="min-w-0">
                <div className="text-sm text-white font-medium truncate" title={topSong.title}>
                  {topSong.title || '—'}
                </div>
                <div className="text-[11px] text-gray-400">
                  {topSong.plays} plays
                </div>
              </div>
            </div>
          ) : (
            <div className="text-xs text-gray-500 italic bg-white/5 rounded-lg p-2">
              {t('topArtistsNoTopSong') || 'No top song found for this period'}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// ─── Main page ───────────────────────────────────────────────────
const TopArtistsPage = ({ user }) => {
  const { scope: rawScope } = useParams();
  const navigate = useNavigate();
  const { t } = useLanguage();

  // Hooks first — never conditionally skipped — to satisfy rules-of-hooks.
  const [rangeKey, setRangeKey] = useState('this_month');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expandedIdx, setExpandedIdx] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [apiError, setApiError] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);

  const isInvalidScope = rawScope && rawScope !== 'greek' && rawScope !== 'global';
  const scope = (rawScope === 'global' ? 'global' : 'greek');

  const SCOPE_META = {
    greek:  { title: t('topArtistsScopeGreece') || 'Greece',  icon: Flag,    color: 'from-blue-500 to-cyan-500' },
    global: { title: t('topArtistsScopeGlobal') || 'Global',  icon: Globe2,  color: 'from-purple-500 to-pink-500' },
  };
  const meta = SCOPE_META[scope];
  const ScopeIcon = meta.icon;

  const fetchChart = useCallback(async () => {
    setLoading(true);
    setApiError(false);
    setExpandedIdx(null);
    try {
      const params = new URLSearchParams({
        scope,
        range: rangeKey,
        limit: '100',
      });
      const resp = await fetch(`${API}/api/charts/top-artists?${params}`);
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      const json = await resp.json();
      setData(json);
    } catch (e) {
      console.error('Top Artists fetch error', e);
      setApiError(true);
    } finally {
      setLoading(false);
    }
  }, [scope, rangeKey]);

  useEffect(() => {
    if (isInvalidScope) return;
    fetchChart();
  }, [fetchChart, isInvalidScope]);

  if (isInvalidScope) {
    return <Navigate to="/top-artists/greek" replace />;
  }

  const filteredItems = useMemo(() => {
    if (!data?.items) return [];
    const s = searchTerm.trim().toLowerCase();
    if (!s) return data.items;
    return data.items.filter(it =>
      (it.display_artist || '').toLowerCase().includes(s) ||
      (it.canonical_artist || '').toLowerCase().includes(s)
    );
  }, [data, searchTerm]);

  const handleExport = async () => {
    if (exportLoading) return;
    setExportLoading(true);
    try {
      const url = `${API}/api/charts/top-artists/export?scope=${scope}&range=${rangeKey}&limit=100`;
      const resp = await fetch(url);
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      const blob = await resp.blob();
      const a = document.createElement('a');
      const objUrl = window.URL.createObjectURL(blob);
      a.href = objUrl;
      a.download = `top-artists-${scope}-${rangeKey}.xlsx`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(objUrl);
      toast.success(t('topArtistsExcelDone') || 'Excel downloaded');
    } catch (e) {
      toast.error(t('topArtistsExcelFailed') || 'Excel export failed');
    } finally {
      setExportLoading(false);
    }
  };

  const rangeLabel = data?.range?.label || t(RANGE_OPTIONS.find(r => r.key === rangeKey)?.labelKey);

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-purple-900 to-gray-900 py-8 px-4">
      <Helmet>
        <title>{t('topArtistsTitle') || 'Top Artists'} {meta.title} | Radio Solutions Apps</title>
        <meta name="description" content={`${t('topArtistsSeoDescription') || 'Top artists on Greek radio.'} (${meta.title})`} />
        <link rel="canonical" href={`https://radio-apps.online/top-artists/${scope}`} />

        {/* OpenGraph */}
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="Radio Solutions Apps" />
        <meta property="og:title" content={`Top Artists ${meta.title} · Radio Solutions Apps`} />
        <meta property="og:description" content={t('topArtistsSeoDescription') || 'Top artists on Greek radio — real-time airplay data.'} />
        <meta property="og:url" content={`https://radio-apps.online/top-artists/${scope}`} />
        <meta property="og:image" content={`https://radio-apps.online/api/og-banner/top-artists-${scope}`} />
        <meta property="og:image:width" content="1600" />
        <meta property="og:image:height" content="840" />
        <meta property="og:image:alt" content={`Top Artists ${meta.title} · Radio Solutions Apps · Measure. Optimize. Perform.`} />

        {/* Twitter Card */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={`Top Artists ${meta.title} · Radio Solutions Apps`} />
        <meta name="twitter:description" content={t('topArtistsSeoDescription') || 'Top artists on Greek radio — real-time airplay data.'} />
        <meta name="twitter:image" content={`https://radio-apps.online/api/og-banner/top-artists-${scope}`} />
        <meta name="twitter:image:alt" content={`Top Artists ${meta.title} · Radio Solutions Apps`} />

        {/* JSON-LD Schema */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "MusicPlaylist",
            "name": `Top Artists ${meta.title}`,
            "description": t('topArtistsSeoDescription') || 'Top artists on Greek radio.',
            "url": `https://radio-apps.online/top-artists/${scope}`,
            "provider": {
              "@type": "Organization",
              "name": "Radio Solutions Apps",
              "url": "https://radio-apps.online"
            }
          })}
        </script>
      </Helmet>

      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="text-center mb-6" data-testid="top-artists-header">
          <h1 className="text-4xl sm:text-5xl font-bold text-white mb-3 tracking-tight">
            {t('topArtistsTitle') || 'Top Artists'}
          </h1>
          <p className="text-gray-400 text-base max-w-xl mx-auto">
            {t('topArtistsSubtitle') || 'The most-played artists on Greek radio.'}
          </p>
        </div>

        {/* Scope tabs */}
        <div className="flex items-center justify-center gap-2 mb-6" data-testid="scope-tabs">
          {['greek', 'global'].map((s) => {
            const M = { ...SCOPE_META_STATIC[s], title: t(s === 'greek' ? 'topArtistsScopeGreece' : 'topArtistsScopeGlobal') };
            const Icon = M.icon;
            const active = scope === s;
            return (
              <button
                key={s}
                data-testid={`scope-tab-${s}`}
                onClick={() => navigate(`/top-artists/${s}`)}
                className={`flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-medium transition-all ${
                  active
                    ? `bg-gradient-to-r ${M.color} text-white shadow-lg shadow-purple-500/30`
                    : 'bg-white/5 text-gray-400 hover:bg-white/10'
                }`}
              >
                <Icon className="w-4 h-4" />
                {M.title}
              </button>
            );
          })}
        </div>

        {/* Range + search + export */}
        <div className="flex flex-wrap items-center gap-3 mb-4">
          <select
            data-testid="range-select"
            value={rangeKey}
            onChange={(e) => setRangeKey(e.target.value)}
            className="bg-white/5 text-white border border-white/10 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-purple-500"
          >
            {RANGE_OPTIONS.map(r => (
              <option key={r.key} value={r.key} className="bg-gray-900">{t(r.labelKey) || r.key}</option>
            ))}
          </select>

          <div className="relative flex-1 min-w-[200px]">
            <Search className="w-4 h-4 text-gray-500 absolute left-3 top-1/2 -translate-y-1/2" />
            <input
              data-testid="artist-search-input"
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder={t('topArtistsSearchPlaceholder') || 'Search artist…'}
              className="w-full bg-white/5 text-white placeholder-gray-500 border border-white/10 rounded-lg pl-10 pr-3 py-2 text-sm focus:outline-none focus:border-purple-500"
            />
          </div>

          <button
            data-testid="export-excel-btn"
            onClick={handleExport}
            disabled={exportLoading || loading || !data?.items?.length}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              exportLoading || loading || !data?.items?.length
                ? 'bg-white/5 text-gray-500 cursor-not-allowed'
                : 'bg-green-600 hover:bg-green-700 text-white'
            }`}
          >
            {exportLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
            {t('topArtistsExcel') || 'Excel'}
          </button>
        </div>

        {/* Range label & count strip */}
        <div className="flex items-center justify-between text-xs text-gray-400 mb-2 px-1">
          <div className="flex items-center gap-2">
            <ScopeIcon className="w-3.5 h-3.5" />
            <span>{rangeLabel}</span>
          </div>
          <span data-testid="chart-count">
            {data?.count ?? 0} {t('topArtistsCountLabel') || 'artists'}
          </span>
        </div>

        {/* Chart body */}
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-8 h-8 text-purple-400 animate-spin" />
          </div>
        ) : apiError ? (
          <div className="text-center py-20 text-gray-400" data-testid="chart-api-error">
            <Music className="w-16 h-16 mx-auto mb-4 opacity-30" />
            <p className="text-lg font-medium mb-2">{t('topArtistsApiError') || 'Connection problem'}</p>
            <button
              onClick={fetchChart}
              className="px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg text-sm"
              data-testid="chart-retry-btn"
            >
              {t('topArtistsRetry') || 'Try again'}
            </button>
          </div>
        ) : filteredItems.length === 0 ? (
          <div className="text-center py-20 text-gray-400" data-testid="chart-empty">
            <Music className="w-16 h-16 mx-auto mb-4 opacity-30" />
            <p>{t('topArtistsEmpty') || 'No artists found for this period.'}</p>
          </div>
        ) : (
          <div className="bg-white/5 backdrop-blur-sm rounded-xl overflow-hidden" data-testid="top-artists-table">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-white/5 text-gray-400 text-xs uppercase">
                  <tr>
                    <th className="py-3 px-3 text-left w-10">#</th>
                    <th className="py-3 px-2 text-left w-14" aria-label="Cover" />
                    <th className="py-3 px-3 text-left">{t('topArtistsColArtist') || 'Artist'}</th>
                    <th className="py-3 px-3 text-center">{t('topArtistsColPlays') || 'Plays'}</th>
                    <th className="py-3 px-3 text-center hidden sm:table-cell">{t('topArtistsColStations') || 'Stations'}</th>
                  </tr>
                </thead>
                <tbody className="text-white">
                  {filteredItems.map((it, idx) => {
                    const expanded = expandedIdx === idx;
                    return (
                      <React.Fragment key={it.canonical_artist}>
                        <tr
                          data-testid={`artist-row-${idx}`}
                          className={`border-t border-white/5 hover:bg-white/5 cursor-pointer transition-colors ${
                            expanded ? 'bg-purple-500/10' : ''
                          }`}
                          onClick={() => setExpandedIdx(expanded ? null : idx)}
                        >
                          <td className="py-3 px-3">
                            <div className="flex items-center gap-1">
                              <span className={`font-bold ${
                                it.rank <= 3 ? 'text-yellow-400' :
                                it.rank <= 10 ? 'text-purple-400' : 'text-gray-400'
                              }`}>
                                {it.rank}
                              </span>
                              {expanded
                                ? <ChevronUp   className="w-3 h-3 text-purple-400" />
                                : <ChevronDown className="w-3 h-3 text-gray-600" />}
                            </div>
                          </td>
                          <td className="py-3 px-2">
                            {it.cover_art_url ? (
                              <img
                                src={it.cover_art_url}
                                alt=""
                                className="w-10 h-10 rounded object-cover"
                                loading="lazy"
                                data-testid={`artist-cover-${idx}`}
                              />
                            ) : (
                              <div className="w-10 h-10 rounded bg-white/10 flex items-center justify-center">
                                <Music className="w-4 h-4 text-white/40" />
                              </div>
                            )}
                          </td>
                          <td className="py-3 px-3 font-medium">
                            <div className="flex items-center gap-2">
                              <span className="truncate max-w-[200px]" title={it.display_artist}>
                                {it.display_artist}
                              </span>
                              {it.rank === 1 && (
                                <Trophy className="w-3.5 h-3.5 text-yellow-400 flex-shrink-0" />
                              )}
                            </div>
                          </td>
                          <td className="py-3 px-3 text-center font-bold text-purple-400" data-testid={`artist-plays-${idx}`}>
                            {it.plays.toLocaleString('el-GR')}
                          </td>
                          <td className="py-3 px-3 text-center hidden sm:table-cell text-gray-300">
                            <div className="inline-flex items-center gap-1">
                              <Radio className="w-3 h-3 text-gray-500" />
                              {it.station_count}
                            </div>
                          </td>
                        </tr>
                        {expanded && (
                          <tr>
                            <td colSpan="5" className="p-0">
                              <ArtistTrendPanel artist={it} rangeKey={rangeKey} t={t} />
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Footer note */}
        <div className="mt-6 text-center">
          <Link to="/radio-chart" className="text-xs text-purple-300 hover:text-purple-200 underline">
            {t('topArtistsSeeAlsoPeoples') || "See also: The People's Chart →"}
          </Link>
        </div>
      </div>
    </div>
  );
};

export default TopArtistsPage;
